import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/shared";
import { fmtMoney } from "@/lib/api";

export const PLAN_DESC = {
  EASY: "Massimo 10 veicoli caricati. I veicoli attivi e disattivati sono entrambi conteggiati.",
  FULL: "Tutte le funzioni Easy più acquisizione e lettura documenti con AI.",
  UPPER: "Tutte le funzioni Full più integrazione Cargos.",
};
const SRC = { listino: ["Listino", "zinc"], personalizzato: ["Personalizzato", "blue"], precedente: ["Prezzo precedente", "amber"] };

export function ListinoCards({ plans, annualMonths = 10 }) {
  return (
    <div className="space-y-2">
      <div className="grid sm:grid-cols-3 gap-3" data-testid="listino-cards">
        {plans.map((p) => (
          <div key={p.code} className="bg-card border border-zinc-800 rounded-lg overflow-hidden flex flex-col" data-testid={`listino-${p.code}`}>
            <div className="p-4 flex-1">
              <div className="font-display font-bold text-lg">{p.code}</div>
              <div className="text-2xl text-brand font-bold" data-testid={`listino-price-${p.code}`}>{fmtMoney(p.monthly_fee_net)}/mese <span className="text-sm text-zinc-400">+ IVA</span></div>
              <div className="text-xs text-zinc-500 mt-1">{p.code === "EASY" && p.max_counted_vehicles ? `Massimo ${p.max_counted_vehicles} veicoli caricati. I veicoli attivi e disattivati sono entrambi conteggiati.` : PLAN_DESC[p.code]}</div>
            </div>
            <div className="border-t border-zinc-800 bg-zinc-900/50 px-4 py-3" data-testid={`listino-setup-${p.code}`}>
              <div className="text-[11px] uppercase tracking-wider text-zinc-400">Attivazione e configurazione</div>
              <div className="font-bold">{fmtMoney(p.setup_price_net)} <span className="text-xs font-normal text-zinc-400">una tantum + IVA</span></div>
            </div>
          </div>))}
      </div>
      <div className="text-xs text-zinc-500" data-testid="listino-renewal">Rinnovo mensile automatico, nessun rinnovo annuale separato. Pagamento annuale anticipato facoltativo: {annualMonths} mensilità (2 in omaggio).</div>
    </div>
  );
}

export function AppliedPrice({ pricing, installments = 0, testid = "applied-price" }) {
  if (!pricing) return null;
  const [t, tone] = SRC[pricing.price_source] || SRC.listino;
  return (
    <div className="text-xs" data-testid={testid}>
      <div>{fmtMoney(pricing.effective_monthly_fee_net)}/mese · attivazione {fmtMoney(pricing.effective_setup_price_net)}{installments > 1 ? ` in ${installments} rate` : ""}</div>
      <StatusBadge text={t} tone={tone} />
    </div>
  );
}

export function PricingFields({ form, setForm, plans }) {
  const p = plans.find((x) => x.code === (form.plan || "easy").toUpperCase());
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" ? null : Number(v));
  const custom = form.custom_setup_price_net != null || form.custom_monthly_fee_net != null;
  const box = "rounded-lg border border-zinc-800 p-4 space-y-3";
  return (
    <div className="space-y-3" data-testid="pricing-fields">
      <div className={box} data-testid="pricing-plan-section">
        <div className="font-semibold text-sm">Canone del piano {p?.code}</div>
        {p && <div className="text-xs text-zinc-400" data-testid="pricing-standard">Standard: {fmtMoney(p.monthly_fee_net)}/mese + IVA</div>}
        <div className="max-w-xs"><Label className="text-xs text-zinc-400">Canone mensile netto per questa società (€) · vuoto = standard</Label><Input type="number" step="any" value={form.custom_monthly_fee_net ?? ""} onChange={(e) => set("custom_monthly_fee_net", num(e.target.value))} className="mt-1" data-testid="custom-monthly-input" /></div>
      </div>
      <div className={box} data-testid="pricing-setup-section">
        <div className="font-semibold text-sm">Costo di attivazione e configurazione</div>
        {p && <div className="text-xs text-zinc-400">Standard: {fmtMoney(p.setup_price_net)} una tantum + IVA</div>}
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs text-zinc-400">Prezzo netto (€) · vuoto = standard</Label><Input type="number" step="any" value={form.custom_setup_price_net ?? ""} onChange={(e) => set("custom_setup_price_net", num(e.target.value))} className="mt-1" data-testid="custom-setup-input" /></div>
          <div><Label className="text-xs text-zinc-400">Numero di rate (0 = unica soluzione, 2-12; solo rinnovo mensile)</Label><Input type="number" min={0} max={12} value={form.setup_installments ?? 0} onChange={(e) => set("setup_installments", e.target.value === "" ? 0 : Number(e.target.value))} className="mt-1" data-testid="setup-installments-input" /></div>
        </div>
      </div>
      {custom && <div className={`${box} grid sm:grid-cols-2 gap-3`} data-testid="pricing-reason-section">
        <div><Label className="text-xs text-zinc-400">Prezzo personalizzato valido dal</Label><Input type="date" value={form.custom_price_valid_from || ""} onChange={(e) => set("custom_price_valid_from", e.target.value)} className="mt-1" data-testid="custom-valid-from-input" /></div>
        <div className="sm:col-span-2"><Label className="text-xs text-zinc-400">Motivazione interna del prezzo personalizzato (obbligatoria, visibile solo al Super Admin)<span className="text-red-400"> *</span></Label><Textarea rows={2} value={form.price_override_reason || ""} onChange={(e) => set("price_override_reason", e.target.value)} className="mt-1" data-testid="price-reason-input" /></div>
      </div>}
    </div>
  );
}

export const pricingPayload = (f) => {
  const s = f.custom_setup_price_net ?? null, m = f.custom_monthly_fee_net ?? null;
  return { price_override_enabled: s != null || m != null, custom_setup_price_net: s, custom_monthly_fee_net: m, price_override_reason: f.price_override_reason || "",
    custom_price_valid_from: f.custom_price_valid_from || "", setup_installments: Number(f.setup_installments || 0) };
};

export function LegacyBox({ company, form, setForm }) {
  const legacy = company?.legacy_pricing;
  if (!legacy) return null;
  return <label className="flex gap-2 text-sm rounded-md border border-amber-500/30 bg-amber-500/10 p-3" data-testid="legacy-pricing-box">
    <input type="checkbox" checked={!!form.adopt_standard_pricing} onChange={(e) => setForm((s) => ({ ...s, adopt_standard_pricing: e.target.checked }))} data-testid="adopt-standard-checkbox" />
    Prezzo precedente in uso ({fmtMoney(legacy.setup_price_net)} attivazione · {fmtMoney(legacy.monthly_fee_net)}/mese). Adotta il nuovo listino.</label>;
}
